import {
  Text,
  Field,
  withDatasourceCheck,
  NextImage,
  ImageField,
  LinkField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import useHelpers from 'src/hooks/helpers';
import Animation from 'components/Generic/Animation';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import styles from './PrimaryHeroBanner.module.scss';

import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { ReactNode } from 'react';

type Fields = {
  backgroundAnimation?: Field<string>;
  backgroundImage?: ImageField;
  title?: Field<string>;
  subtitle?: Field<string>;
  primaryCtaLink?: LinkField;
  secondaryCtaLink?: LinkField;
};

type PrimaryHeroBannerProps = ComponentProps & {
  fields: Fields;
};

const PrimaryHeroBanner = ({ fields, rendering }: PrimaryHeroBannerProps): JSX.Element => {
  const desktopAnimation = fields.backgroundAnimation?.value;
  const { anchorId } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const isValueExists = (value?: LinkField): boolean => {
    return !!isExperienceEditor || !!value?.value?.text || !!value?.value?.href;
  };
  const deviceType: string = useWindowDimensions();

  const BannerMediaContent = (): ReactNode => {
    if (desktopAnimation && deviceType === 'desktop') {
      return (
        <div className={styles['background-video-wrapper']}>
          <Animation
            loop={false}
            visible={true}
            autoplay={true}
            animationData={desktopAnimation}
            layout={{ fit: 'fit-width', align: [1, 0] }}
          />
        </div>
      );
    } else {
      return (
        <div className={styles['background-image-wrapper']}>
          <NextImage field={fields.backgroundImage} fill loading="eager" />
        </div>
      );
    }
  };

  return (
    <section
      id={anchorId}
      className={`${styles['primary-hero-banner-section']} d-flex flex-column primary-hero-banner-section`}
      data-e2e-component-uid={rendering.uid}
    >
      <BannerMediaContent />
      <div className={`${styles['text-block']} container text-block mb-md-n7 mt-n5 mt-md-auto`}>
        <div className="row w-100 pe-3 pe-md-0">
          <div
            className={`${styles['inner-wrapper']} background-white col-12 col-md-12 col-lg-7 pe-md-4 py-4 pb-md-6 pt-xl-7 pb-xl-8 pe-xl-7`}
          >
            <Text field={fields.title} tag="h1" />
            {fields.subtitle && fields.subtitle.value && (
              <Text field={fields.subtitle} className="mt-2 body-large" tag="p" />
            )}
            {(isValueExists(fields.primaryCtaLink) || isValueExists(fields.secondaryCtaLink)) && (
              <div className={`${styles['links-wrapper']} d-flex mt-5`}>
                {isValueExists(fields.primaryCtaLink) && (
                  <AbrdnButton
                    buttonType="primary"
                    linkField={fields.primaryCtaLink}
                    className="mt-3 me-sm-3 mt-md-0"
                  />
                )}
                {isValueExists(fields.secondaryCtaLink) && (
                  <AbrdnButton
                    buttonType="secondary"
                    linkField={fields.secondaryCtaLink}
                    className="mt-3 mt-md-0"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`${styles['break-line']} background-white pt-4 pt-md-7 pt-xl-7`} />
    </section>
  );
};

export default withDatasourceCheck()<PrimaryHeroBannerProps>(PrimaryHeroBanner);
